import type { Image } from "./types";
import { buildImageUrl, type ImageVariant } from "./images";
import { imageAlt } from "@/lib/images/alt";

// Image fields live on every refType branch (the backend exposed them on
// Course / Event / Podcast after the initial release; OTT had them all along).
// Pick the first available Image node, then resolve it through buildImageUrl so
// the result is a Cloudflare Images delivery URL when the tenant has CF
// configured, and the plain `Image.uri` otherwise. Callers thread the
// per-tenant `cloudflareAccountHash` (from loadPortalConfig()) in.
//
// Structural (not `Pick<Module>`) so any selection that includes the image
// branches fits — a full Module, a catalog card, or a Cart line item (which
// selects only `{ id image heroImage }` per branch).
// `Pick<Image, "hash" | "uri" | "alt">`, not `Image`: hash/uri is all buildImageUrl
// reads and alt (optional, so a selection without it still fits) is all the *Alt
// helpers read — which lets the structural JSON-LD inputs in lib/seo.ts (which
// select no id) go through the same selection instead of hand-rolling the chain.
type ImageNode = Pick<Image, "hash" | "uri" | "alt" | "caption">;
type ImageBranch = { image?: ImageNode | null; heroImage?: ImageNode | null } | null;
type WithImageBranches = {
  ott?: ImageBranch;
  course?: ImageBranch;
  event?: ImageBranch;
  podcast?: ImageBranch;
  post?: ImageBranch;
  /** Module-level social/OG image — the LAST-RESORT poster (see modulePoster). */
  seoImage?: ImageNode | null;
};

function firstImage(...nodes: (ImageNode | null | undefined)[]): ImageNode | null {
  for (const n of nodes) {
    if (n?.uri) return n;
  }
  return null;
}

function posterNode(m: WithImageBranches): ImageNode | null {
  return firstImage(
    m.ott?.image,
    m.ott?.heroImage,
    m.course?.image,
    m.course?.heroImage,
    m.event?.image,
    m.event?.heroImage,
    m.podcast?.image,
    m.podcast?.heroImage,
    m.post?.image,
    m.post?.heroImage,
    // Last resort: the social/OG image. It IS the module's representative
    // artwork, and lhub-admin's event screen currently offers no other image
    // upload — better a real poster than the grey name-fallback frame.
    m.seoImage,
  );
}

function heroNode(m: WithImageBranches): ImageNode | null {
  return firstImage(
    m.ott?.heroImage,
    m.course?.heroImage,
    m.event?.heroImage,
    m.podcast?.heroImage,
    m.post?.heroImage,
    m.ott?.image,
    m.course?.image,
    m.event?.image,
    m.podcast?.image,
    m.post?.image,
  );
}

export function modulePoster(
  m: WithImageBranches,
  cloudflareAccountHash: string | null = null,
  variant: ImageVariant = "public",
): string | null {
  return buildImageUrl(posterNode(m), variant, cloudflareAccountHash);
}

export function moduleHero(
  m: WithImageBranches,
  cloudflareAccountHash: string | null = null,
  variant: ImageVariant = "xl",
): string | null {
  return buildImageUrl(heroNode(m), variant, cloudflareAccountHash);
}

/**
 * Alt text for the image modulePoster / moduleHero would show — read from THAT
 * node, because the chains fall through: a module without `heroImage` renders
 * `image` as its hero and must not carry `heroImage`'s text. Tri-state via
 * imageAlt; callers pass the module name as the fallback description.
 */
export function modulePosterAlt(m: WithImageBranches, fallback?: string | null): string {
  return imageAlt(posterNode(m)?.alt, fallback);
}

export function moduleHeroAlt(m: WithImageBranches, fallback?: string | null): string {
  return imageAlt(heroNode(m)?.alt, fallback);
}

/**
 * The visible credit for the image moduleHero would show — read from THAT
 * node for the same reason as the alt readers. Null when the uploader set
 * none; the article renders nothing then (a credit is a fact, not a caption to
 * invent). Selections without `caption` fit (optional on Image) and read null.
 */
export function moduleHeroCaption(m: WithImageBranches): string | null {
  return heroNode(m)?.caption ?? null;
}

/**
 * A content type whose `Image` is a TOP-LEVEL field rather than nested under a
 * refType branch. `CommunicationPost` is the one today.
 */
type WithFlatImage = { image?: ImageNode | null } | null | undefined;

/**
 * The flat counterparts to modulePoster/moduleHero.
 *
 * There is no branch to pick here, so all these add over a bare `buildImageUrl`
 * is the VARIANT CONVENTION — `public` for a card poster, `xl` for an article
 * hero — pinned in one place instead of re-decided at each call site. That is
 * not cosmetic: news originally shipped reading `image.uri` directly, which
 * skipped Cloudflare Images entirely and handed `next/image` a raw backend URL
 * whose host varies per deploy. It rendered nothing on any portal whose asset
 * host is not in the optimizer allow-list, and served full-size originals
 * (477 KB where the CF `thumbnail` variant is 45 KB) everywhere else.
 *
 * The reason that happened is worth keeping: modulePoster's parameter type is
 * shaped for Module branches, so a CommunicationPost did not fit it and there
 * was no drop-in to reach for. Now there is.
 */
export function contentPoster(
  entity: WithFlatImage,
  cloudflareAccountHash: string | null = null,
  variant: ImageVariant = "public",
): string | null {
  return buildImageUrl(entity?.image ?? null, variant, cloudflareAccountHash);
}

export function contentHero(
  entity: WithFlatImage,
  cloudflareAccountHash: string | null = null,
  variant: ImageVariant = "xl",
): string | null {
  return buildImageUrl(entity?.image ?? null, variant, cloudflareAccountHash);
}
